import { apiGet } from "@softmax/observatory-client/client";

import { ApiError } from "@softmax/observatory-client/transport";

import type { Schemas } from "@softmax/observatory-client/types";

import { queryOptions } from "@tanstack/react-query";

import { type DirectoryEntryLike } from "@softmax/web-features/site/records";
import { publicRead } from "@softmax/web-features/data/public-api";
import { leagueRoute, slugifyLeagueName } from "@softmax/web-features/leagues";

const options = (signal: AbortSignal) => ({
  elevatedPrivileges: false,
  signal,
});

export function coworldDirectoryQueryOptions(query: string) {
  const parameters = new URLSearchParams({ q: query, limit: "12" });
  return queryOptions({
    queryKey: ["site", "coworld-directory", "public", query],
    queryFn: async ({ signal }) => {
      const page = await publicRead<Schemas["ListedGameCatalogPage"]>({
        path: `/v2/leagues/catalog/games?${parameters}`,
        signal,
      });
      return page.leagues.map((league): DirectoryEntryLike => ({
        league_id: league.id,
        default_league_id: league.game.default_league_id ?? null,
        route: leagueRoute(league),
        corpusSlug: slugifyLeagueName(
          league.game.coworld_name ?? league.game.name,
        ),
        coworld: league.game.coworld_name ?? null,
        name: league.game.name,
      }));
    },
    staleTime: 60_000,
  });
}

export function policySearchQueryOptions({
  query,
  limit,
  mine = false,
}: {
  query: string;
  limit: number;
  mine?: boolean;
}) {
  return queryOptions({
    queryKey: [
      "site",
      "search",
      "policies",
      { query, limit, mine, elevatedPrivileges: false },
    ],
    queryFn: ({ signal }) =>
      apiGet<Schemas["V2PolicyVersionRow"][]>(
        `/v2/policy-versions?${mine ? "mine=true&" : ""}q=${encodeURIComponent(query)}&limit=${limit}`,
        options(signal),
      ),
  });
}

export function forumSearchQueryOptions({
  corpusName,
  query,
  limit,
}: {
  corpusName: string;
  query: string;
  limit: number;
}) {
  return queryOptions({
    queryKey: [
      "site",
      "search",
      "forum",
      { corpusName, query, limit, elevatedPrivileges: false },
    ],
    queryFn: ({ signal }) =>
      apiGet<Schemas["ForumSearchPage"]>(
        `/v2/forums/${encodeURIComponent(corpusName)}/search?q=${encodeURIComponent(query)}&limit=${limit}`,
        options(signal),
      ),
  });
}

export function wikiSearchQueryOptions({
  corpusName,
  query,
  limit,
}: {
  corpusName: string;
  query: string;
  limit: number;
}) {
  return queryOptions({
    queryKey: [
      "site",
      "search",
      "wiki",
      { corpusName, query, limit, elevatedPrivileges: false },
    ],
    queryFn: ({ signal }) =>
      apiGet<Schemas["WikiSearchPage"]>(
        `/v2/wikis/${encodeURIComponent(corpusName)}/search?q=${encodeURIComponent(query)}&limit=${limit}`,
        options(signal),
      ),
  });
}

export function episodeSearchQueryOptions(episodeId: string) {
  return queryOptions({
    queryKey: [
      "site",
      "search",
      "episode",
      { episodeId, elevatedPrivileges: false },
    ],
    queryFn: ({ signal }) =>
      apiGet<Schemas["V2EpisodeDetail"]>(
        `/v2/episodes/${episodeId}`,
        options(signal),
      ).catch((error: unknown) => {
        if (error instanceof ApiError && error.status === 404) return null;

        throw error;
      }),
  });
}
